import React, { useState, useEffect, useContext } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { getBarcodeConfig, setBarcodeConfig } from '../../Actions';
import { AlertContext } from '../../Context/AlertContextProvider';

// Friendly labels for the whitelisted source columns returned by the server.
const COLUMN_LABELS = {
  unique_source_id: 'Source System ID',
  alternate_product_sku: 'Alternate Product SKU',
  product_sku: 'Product SKU',
  color_number: 'Color Number',
};

const BarcodeSettings = () => {
  const { handleAlertOpen, setMessageType, setMessage } =
    useContext(AlertContext);

  const [enabled, setEnabled] = useState(false);
  const [sourceColumn, setSourceColumn] = useState('unique_source_id');
  const [prefix, setPrefix] = useState('');
  const [symbology, setSymbology] = useState('CODE128');
  const [sourceColumns, setSourceColumns] = useState([]);

  useEffect(() => {
    getBarcodeConfig()
      .then((res) => {
        const data = res.data?.data || {};
        setEnabled(!!data.enabled);
        setSourceColumn(data.sourceColumn || 'unique_source_id');
        setPrefix(data.prefix || '');
        setSymbology(data.symbology || 'CODE128');
        setSourceColumns(res.data?.source_columns || []);
      })
      .catch(() => {
        setMessage('Failed to load barcode settings');
        setMessageType('error');
        handleAlertOpen();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    setBarcodeConfig({ enabled, sourceColumn, prefix, symbology })
      .then(() => {
        setMessage('Barcode settings updated');
        setMessageType('success');
        handleAlertOpen();
      })
      .catch(() => {
        setMessage('Failed to update barcode settings');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  return (
    <Grid
      item
      xs={12}
      sm={12}
      md={6}
      lg={6}
      className="admin__grid-item"
      style={{ display: 'flex' }}
    >
      <Paper
        elevation={5}
        style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Typography variant="h4" className="header-text">
          Product Barcode
        </Typography>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            margin: '20px 16px',
          }}
        >
          <ToggleButtonGroup
            value={enabled ? 'on' : 'off'}
            exclusive
            size="small"
            onChange={(e, newValue) => {
              if (newValue !== null) setEnabled(newValue === 'on');
            }}
          >
            <ToggleButton
              value="on"
              style={
                enabled
                  ? { width: '50px', backgroundColor: '#4caf50', color: '#FFFFFF' }
                  : { width: '50px', backgroundColor: '#00000017', color: 'initial' }
              }
            >
              Yes
            </ToggleButton>
            <ToggleButton
              value="off"
              style={
                !enabled
                  ? { width: '50px', backgroundColor: '#f44336', color: '#FFFFFF' }
                  : { width: '50px', backgroundColor: '#00000017', color: 'initial' }
              }
            >
              No
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body1">Show barcode on product page?</Typography>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            alignItems: 'center',
            margin: '0 16px 20px',
          }}
        >
          <FormControl
            variant="filled"
            style={{ width: '80%', minWidth: 200, textAlign: 'left' }}
            disabled={!enabled}
          >
            <InputLabel id="barcode-source-label">Barcode Data Column</InputLabel>
            <Select
              labelId="barcode-source-label"
              value={sourceColumn}
              onChange={(e) => setSourceColumn(e.target.value)}
            >
              {sourceColumns.map((col) => (
                <MenuItem key={col} value={col}>
                  {COLUMN_LABELS[col] || col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            variant="filled"
            label="Prefix (optional)"
            placeholder="e.g. P"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            disabled={!enabled}
            style={{ width: '80%', minWidth: 200 }}
            size="small"
            helperText="Prepended to the column value, e.g. P + 235893 → P235893"
          />

          <Button
            variant="contained"
            style={{ background: '#EEEEEE', color: '#009D47' }}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>

        <Accordion style={{ marginTop: 'auto' }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="barcode-content"
          >
            <Typography variant="h5">Product Barcode</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Display a scannable barcode at the bottom of the product page. The
              barcode is generated from the selected color/option using the data
              column you choose, so external systems (e.g. RFMS) can be searched
              with a barcode scanner. Use the prefix field if your system expects
              a leading character before the value.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Grid>
  );
};

export default BarcodeSettings;
